/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import { MailOutlined } from '@ant-design/icons'
import type { ReactNode } from 'react'
import colors from '@sportninja/common/constants/appColors'
import type { BalanceSlotInvitee } from '@sportninja/common/services/teamBalance'
import { weightSemiBold } from 'src/components/css'
import RichTooltip from 'src/components/ts-components/RichTooltip'
import type { BalanceSlotDisplayStatus } from '../utils/balanceFinder'
import { displayStatusLabel } from '../utils/balanceFinder'
import { balanceText } from '../utils/balanceI18n'
import { inviteSentFact, inviteSourceFact } from '../utils/slotTypeDisplay'

/**
 * Dot hues mirror the RegistrationTable status language (200-tier accents).
 *
 * Exported because the STATUS column's header legend paints the same dots: a
 * legend whose swatches drift from the chips it explains is worse than none.
 */
export const STATUS_DOT_COLORS: Record<BalanceSlotDisplayStatus, string> = {
  open: colors.NEUTRAL_100,
  // Jeff, Oct 1 — held for an invited player: must not read as Invoiced (blue) or Open (grey).
  invited: colors.VIOLET_200,
  pending: colors.WARNING_200,
  confirmed: colors.INFO_200,
  paid: colors.SUCCESS_200,
  cancelled: colors.ERROR_200,
}

type StatusDotLabelProps = {
  dotColor: string
  label: string
  testId?: string
}

/** Shared dot + label status treatment (matches the registration table). */
export const StatusDotLabel = ({
  dotColor,
  label,
  testId,
}: StatusDotLabelProps) => (
  <span
    css={css`
      display: inline-flex;
      align-items: center;
      gap: 8px;
      font-size: 12px;
      font-weight: 600;
      letter-spacing: 0.4px;
      text-transform: uppercase;
      white-space: nowrap;
      color: ${colors.NEUTRAL_30};
    `}
    data-testid={testId}
  >
    <span
      css={css`
        width: 8px;
        height: 8px;
        border-radius: 50%;
        flex-shrink: 0;
        background: ${dotColor};
      `}
    />
    {label}
  </span>
)

type BalanceStatusChipProps = {
  /**
   * The slot's lifecycle status, read through `slotDisplayStatus()` so the chip,
   * the grid sort and the filter can never disagree about what a row says.
   */
  status: BalanceSlotDisplayStatus
  /**
   * Sep 23 — a team official's line: its pending / invoiced / paid tooltips speak of
   * the official's share, not a player's. Everything else about the chip is the same.
   */
  official?: boolean
  /**
   * Jeff, Oct 2 — who an invited slot is held for. Its tooltip then opens with
   * this row's own facts (when the invite went out and to whom) before what
   * Invited means. Read for the `invited` status only.
   */
  invitee?: BalanceSlotInvitee | null
}

/** The address inside the fact's sentence, in semibold — wherever the copy puts it. */
const withEmphasis = (text: string, part: string): ReactNode => {
  const at = text.indexOf(part)
  if (at < 0) {
    return text
  }

  return (
    <>
      {text.slice(0, at)}
      <span css={emphasisStyle}>{part}</span>
      {text.slice(at + part.length)}
    </>
  )
}

/**
 * The status column is where the sheet is asked "what does this mean?" most
 * often — six one-word states covering a slot's whole lifecycle, several of
 * which (open / invited / invoiced) are hard to tell apart from the label alone.
 *
 * Each state therefore carries two lines: what it MEANS and what happens NEXT.
 * The second half is the one that stops a user opening another screen to find
 * out, so every state has one — including `paid`, whose answer is "nothing".
 */
const STATUS_TIP_KEYS: Record<
  BalanceSlotDisplayStatus,
  { tip: string; next: string }
> = {
  open: {
    tip: 'common:balanceStatusTipOpen',
    next: 'common:balanceStatusNextOpen',
  },
  invited: {
    tip: 'common:balanceStatusTipInvited',
    next: 'common:balanceStatusNextInvited',
  },
  pending: {
    tip: 'common:balanceStatusTipPending',
    next: 'common:balanceStatusNextPending',
  },
  confirmed: {
    tip: 'common:balanceStatusTipConfirmed',
    next: 'common:balanceStatusNextConfirmed',
  },
  paid: {
    tip: 'common:balanceStatusTipPaid',
    next: 'common:balanceStatusNextPaid',
  },
  cancelled: {
    tip: 'common:balanceStatusTipCancelled',
    next: 'common:balanceStatusNextCancelled',
  },
}

/**
 * Sep 23 — the three statuses an official's line reaches, in an official's terms.
 * Their "next" copy is the player's: invoicing, reminding and settling work the same.
 */
const OFFICIAL_TIP_KEYS: Partial<Record<BalanceSlotDisplayStatus, string>> = {
  pending: 'common:balanceStatusTipPendingOfficial',
  confirmed: 'common:balanceStatusTipConfirmedOfficial',
  paid: 'common:balanceStatusTipPaidOfficial',
}

const BalanceStatusChip = ({
  status,
  official = false,
  invitee = null,
}: BalanceStatusChipProps) => {
  // The tooltip heading is the SAME string the chip shows — a heading that can
  // drift from the label it explains is worse than no heading.
  const label = displayStatusLabel(status)
  const keys = STATUS_TIP_KEYS[status] ?? STATUS_TIP_KEYS.open
  // The specific before the general: "integrate both" (Jeff, Oct 2).
  const sent = status === 'invited' ? inviteSentFact(invitee) : null
  // Jeff, Oct 1 — imported from a previous season: which one, on its own line in
  // the same box.
  const source = status === 'invited' ? inviteSourceFact(invitee) : null

  return (
    <RichTooltip
      title={label}
      fact={
        sent || source
          ? {
              content: (
                <>
                  {sent ? withEmphasis(sent.text, sent.email) : null}
                  {source ? (
                    <span
                      css={sourceLineStyle}
                      data-testid='invite-source-fact'
                    >
                      {source}
                    </span>
                  ) : null}
                </>
              ),
              text: [sent?.text, source].filter(Boolean).join('. '),
              color: STATUS_DOT_COLORS.invited,
              icon: <MailOutlined />,
            }
          : undefined
      }
      body={balanceText(
        (official ? OFFICIAL_TIP_KEYS[status] : undefined) ?? keys.tip
      )}
      next={balanceText(keys.next)}
    >
      <StatusDotLabel
        dotColor={STATUS_DOT_COLORS[status] ?? STATUS_DOT_COLORS.open}
        label={label}
        testId={`balance-status-${status}`}
      />
    </RichTooltip>
  )
}

export default BalanceStatusChip

const emphasisStyle = css`
  ${weightSemiBold};
`

/** The season an imported invitee came from, under when and where it was sent. */
const sourceLineStyle = css`
  display: block;
`
